// Client layer (TS-2.5.03) - BRD section 8.6, UC-22, decisions.md D-207.
//
// THIS PANEL CAN SEE THE SERVER'S STATE NOW, WHICH D-234 RECORDED THAT IT COULD NOT. It was
// write-only: a profile-code box, a save button, and a permanently displayed "no active field-map
// profile" message that was a static prompt rather than a fact, because `configuration.ts` exposed
// only `PUT /api/config/field-maps`. D-234 also recorded the alternative that was rejected -
// inferring the state from a roadmap read's D-207 failure - on the grounds that the inference is
// unreliable in both directions. `GET /api/config/field-maps` answers the question directly.
//
// SO THE D-207 PROMPT IS NOW CONDITIONAL, and that is the point of closing the gap: a deployment
// with no profile still gets the message that explains why nothing renders, and a deployment that
// has one gets a list of what it has instead of being told, wrongly, that it has nothing.
//
// SELECTING A PROFILE READS IT WHOLE. The summary a list row shows carries counts rather than
// bindings - forty-odd per profile, none of which a row displays - so opening one is a second
// read. It is fired on selection rather than eagerly for every row, because a deployment with
// several vendor profiles would otherwise fetch every binding of every one of them to draw a list.
//
// AND THE PANEL CAN NOW SAY WHICH ONE IS ACTIVE (D-254, closing the gap D-253 recorded). The
// summary carries `isActive`, stamped server-side from the `source.activeProfile` setting every
// reader resolves its source context from, so the active row is badged instead of described. The
// note D-253 stood in with survives for one case only: the setting naming none of the stored
// profiles, which is the state a deployment that never named one is in.
//
// AND ACTIVATION IS NOW A REAL ACTION RATHER THAN A CLAIM THE SAVE FORM MADE. D-254 had to correct
// this panel's copy because nothing in the save path wrote `source.activeProfile`: a single-profile
// deployment worked through three readers' fallback to the only profile there is, not through an
// activation. `PUT /api/config/field-maps/active` closes that - it is the act D-207's documented
// first run assumed an administrator could perform - so the panel now offers the two acts
// separately: a plain "save a profile" form, and a "set active" action on each stored row that is
// not already active.
//
// THE ACTIVATION IS CONFIRMED AND THE SAVE IS NOT, which is the house rule about blocking dialogs
// applied rather than bent: a confirm is for a change whose effect reaches past the screen it was
// made on. Saving stores a mapping nothing may yet read; activating one moves the setting every
// roadmap read, import, export and publish resolves its source from, for everybody connected.
//
// The save form still submits an empty binding set, because this batch exposes no binding editor: a
// profile's bindings are authored elsewhere (D-126's checked-in map, or an import's own preview),
// and a UI that could half-write them would be a worse thing than one that cannot write them at all.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { FieldMapProfile, FieldMapProfileSummary } from "../../api/generated/contracts";
import { configurationApi } from "../../api/configuration";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { WarningsBanner } from "../../components/WarningsBanner";
import { issueBannerItems } from "./lib/adminBanners";
import { formatDateTime } from "./lib/adminFormat";
import { strings } from "../../strings";

const F = strings.admin.fieldMaps;

export function FieldMapPanel() {
  const [profiles, setProfiles] = useState<FieldMapProfileSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selected, setSelected] = useState<FieldMapProfile | null>(null);
  const [opening, setOpening] = useState(false);

  const [profileCode, setProfileCode] = useState("");
  const [sourceKind, setSourceKind] = useState("Workbook");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [banners, setBanners] = useState<ReturnType<typeof issueBannerItems>>([]);
  const [saved, setSaved] = useState(false);

  // Two codes rather than one flag: `pendingCode` is what the confirm dialog is ASKING about, and
  // `activatingCode` is what a request is in flight FOR. Keeping them apart is what lets the dialog
  // close the moment it is answered - returning focus to the row's button, which then says what it
  // is doing - instead of hanging open over a request nobody can cancel.
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [activatingCode, setActivatingCode] = useState<string | null>(null);
  const [activated, setActivated] = useState(false);

  // A FAILED ACTIVATION GETS ITS OWN ERROR SLOT, not the panel's shared one, because the shared
  // one's heading says a profile could not be LOADED. Reporting "could not be loaded" over a
  // refused write would be the same class of copy-versus-behaviour gap this panel has just spent
  // two passes closing.
  const [activationError, setActivationError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void configurationApi
      .listFieldMaps()
      .then((next) => {
        setProfiles(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setProfiles(null);
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The field-map profiles could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  async function open(code: string) {
    setSelectedCode(code);
    setSelected(null);
    setOpening(true);
    try {
      setSelected(await configurationApi.getFieldMap(code));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The field-map profile could not be loaded."));
    } finally {
      setOpening(false);
    }
  }

  async function handleSave() {
    if (profileCode.trim().length === 0) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    setActivated(false);
    try {
      const result = await configurationApi.saveFieldMap({
        profile: {
          profileCode: profileCode.trim(),
          sourceKind,
          version: "1",
          entities: [],
          fields: [],
          unknownFields: "ReportAsIgnored",
        },
        observedFields: [],
      });
      setBanners(issueBannerItems(result.issues));
      if (result.saved) {
        setSaved(true);
        refresh();
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The field-map profile could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  // THE LIST IS REFETCHED RATHER THAN PATCHED IN PLACE. The response carries the activated summary
  // with its flag set, but the row that STOPPED being active is not in that response - and a table
  // that showed two badges because it only knew about one of the two changes would be worse than
  // one that waited for the read. The server is the only thing that knows which row is active.
  async function confirmActivate(profileCode: string) {
    setPendingCode(null);
    setActivatingCode(profileCode);
    setActivationError(null);
    setActivated(false);
    setSaved(false);
    setBanners([]);
    try {
      const result = await configurationApi.activateFieldMap({ profileCode });
      setBanners(issueBannerItems(result.issues));
      if (result.saved) {
        setActivated(true);
        refresh();
      }
    } catch (cause) {
      setActivationError(
        cause instanceof ApiError ? cause : new ApiError("network", 0, "The field-map profile could not be activated."),
      );
    } finally {
      setActivatingCode(null);
    }
  }

  const hasNoProfile = profiles !== null && profiles.length === 0;

  // D-254. `isActive` is stamped by ConfigurationManager from the `source.activeProfile` setting -
  // the same value RoadmapManager, IngestionManager, ExportManager and PublicationManager each
  // resolve their source from - so the badge below and the profile a roadmap is actually built
  // from cannot disagree. When the setting names none of the stored profiles (its unseeded
  // default) every row reports false, and the note takes the badge's place rather than a row
  // being promoted on a guess - the inference D-234 rejected.
  const noneActive = profiles !== null && profiles.length > 0 && !profiles.some((profile) => profile.isActive);

  return (
    <section className="eg-admin-field-maps">
      <h2>{F.heading}</h2>

      {loading && profiles === null && (
        <div role="status" aria-live="polite">
          {F.loading}
        </div>
      )}

      {hasNoProfile && (
        <div role="status" aria-live="polite" className="eg-admin-field-maps__no-active">
          <p className="eg-admin-field-maps__no-active-title">{F.noActiveProfileTitle}</p>
          <p>{F.noActiveProfile}</p>
        </div>
      )}

      {profiles !== null && profiles.length > 0 && (
        <table>
          <caption>{F.profilesHeading}</caption>
          <thead>
            <tr>
              <th scope="col">{F.profileCode}</th>
              <th scope="col">{F.sourceKind}</th>
              <th scope="col">{F.bindings}</th>
              <th scope="col">{F.updatedAt}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile) => (
              <tr key={profile.profileCode}>
                <td>
                  {profile.profileCode}
                  {profile.isActive && (
                    <span className="eg-admin__badge--active" title={F.activeBadgeLabel}>
                      {F.activeBadge}
                    </span>
                  )}
                </td>
                <td>{profile.sourceKind}</td>
                <td>
                  {profile.entityBindingCount} / {profile.fieldBindingCount}
                </td>
                <td>{formatDateTime(profile.updatedAtUtc)}</td>
                <td>
                  <button type="button" onClick={() => void open(profile.profileCode)}>
                    {F.view}
                  </button>
                  {/* No "set active" on the row that already is one: the action would be a no-op
                      the server would happily accept, and an enabled control that changes nothing
                      is how a screen teaches somebody that its controls are decorative. */}
                  {!profile.isActive && (
                    <button
                      type="button"
                      className="eg-admin__btn--primary"
                      disabled={activatingCode !== null}
                      onClick={() => setPendingCode(profile.profileCode)}
                    >
                      {activatingCode === profile.profileCode ? F.activating : F.setActive}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The note D-253 wrote as a stand-in for a badge now appears only when there IS nothing to
          badge: the active-source setting names no stored profile. That is a real and actionable
          state - it is the setting's unseeded default - and saying so is more useful than a table
          that simply shows no badge and leaves an admin wondering whether the screen knows. */}
      {noneActive && <p className="eg-admin__hint">{F.noneActiveNote}</p>}

      <ConfirmDialog
        open={pendingCode !== null}
        title={F.setActiveConfirmTitle}
        body={F.setActiveConfirmBody}
        confirmLabel={F.setActiveConfirmConfirm}
        cancelLabel={F.setActiveConfirmCancel}
        onConfirm={() => {
          if (pendingCode) void confirmActivate(pendingCode);
        }}
        onCancel={() => setPendingCode(null)}
      />

      {selectedCode && (
        <div className="eg-admin-field-maps__detail">
          <h3>{selectedCode}</h3>
          {opening && <p>{F.loading}</p>}
          {selected && (
            <ul>
              {selected.entities.map((entity) => (
                <li key={entity.targetEntity}>
                  {entity.targetEntity} → {entity.sourceCollection}
                </li>
              ))}
              {selected.fields.map((field) => (
                <li key={`${field.targetEntity}.${field.targetField}`}>
                  {field.targetEntity}.{field.targetField} → {field.sourcePath}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <WarningsBanner items={banners} />

      <h3>{F.saveHeading}</h3>
      <p className="eg-admin__hint">{F.saveDescription}</p>

      <div className="eg-admin__form">
        <FormField id="eg-field-map-profile-code" label={F.profileCode} required>
          <input
            type="text"
            required
            value={profileCode}
            onChange={(event) => setProfileCode(event.target.value)}
            {...formFieldControlProps("eg-field-map-profile-code")}
          />
        </FormField>

        <FormField id="eg-field-map-source-kind" label={F.sourceKind}>
          <input
            type="text"
            value={sourceKind}
            onChange={(event) => setSourceKind(event.target.value)}
            {...formFieldControlProps("eg-field-map-source-kind")}
          />
        </FormField>

        <div className="eg-admin__actions">
          <button
            type="button"
            className="eg-admin__btn--primary"
            disabled={saving || profileCode.trim().length === 0}
            onClick={() => void handleSave()}
          >
            {saving ? F.saving : F.save}
          </button>
        </div>
      </div>

      {error && (
        <div role="alert">
          <p>{F.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {activationError && (
        <div role="alert">
          <p>{F.activationErrorTitle}</p>
          <p>{activationError.message}</p>
        </div>
      )}

      {saved && <p>{F.savedConfirmation}</p>}
      {activated && (
        <p role="status" aria-live="polite">
          {F.activatedConfirmation}
        </p>
      )}
    </section>
  );
}
